// Angular
import { Component, OnInit, Inject, ViewChild, ElementRef, HostBinding, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// Libraries
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

// Models
import { AppCleanup } from '../../../../shared/models';
import { ChannelViewModel } from '../../../../modules/unit-config/models/unit-config-view.model';

// Services
import { UnitConfigService, ValidationObject } from '../../../../modules/unit-config/services/unit-config.service';
import { SpinnerService } from '../../../../shared/components/spinner/services/spinner.service';

// Helpers
import { FormHelper } from '../../../../shared/helpers';

@Component({
	selector: 'channel-expression-dialog',
	templateUrl: './channel-expression-dialog.component.html',
	styleUrls: ['./channel-expression-dialog.component.scss'],
})
export class ChannelExpressionDialogComponent extends AppCleanup implements OnInit, OnDestroy {
	@HostBinding('class') classes = 'component-container';
	componentTitle: string = 'Channel expression';
	form: FormGroup;
	isEdit: boolean = false;
	expression: string;
	currentChannel: ChannelViewModel;
	channels: ChannelViewModel[];
	numbers = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '.'];
	operators = ['+', '-', '*', '/', '%', '(', ')', 'ABS', 'SQRT', 'POW'];
	errorMessage;
	insertedChannels: string[] = [];
	@ViewChild('expressionInput', { static: false }) expressionInput: ElementRef;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: { expression: string; currentChannel: ChannelViewModel; channels: ChannelViewModel[] },
		private dialogRef: MatDialogRef<ChannelExpressionDialogComponent>,
		private unitService: UnitConfigService,
		private formBuilder: FormBuilder,
		private spinnerService: SpinnerService
	) {
		super();
	}

	ngOnInit() {
		if (this.data?.expression) {
			// If edit
			this.isEdit = true;
			this.expression = this.data.expression;
		}

		if (this.data?.channels) {
			this.channels = this.data.channels;
		}

		if (this.data?.currentChannel) {
			this.currentChannel = this.data.currentChannel;
		}

		this.form = this.formBuilder.group({
			expression: [this.isEdit ? this.expression : null, [Validators.required, Validators.maxLength(100)]],
		});
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	controlHasError(controlName: string, validationType: string): boolean {
		return FormHelper.controlHasError(this.form, controlName, validationType);
	}

	insertChannel(channelName: string) {
		event.preventDefault();
		this.insertedChannels.push(channelName);
	}

	insertCharacter(event, character: string) {
		event.preventDefault();
		const start = this.expressionInput.nativeElement.selectionStart;
		const end = this.expressionInput.nativeElement.selectionEnd;
		const value = this.expressionInput.nativeElement.value;
		let expression;
		let cursorStart;
		let cursorEnd;
		// update the value with our text inserted
		if (character === 'ABS' || character === 'SQRT') {
			character = `${character}(x)`;
			expression = value.slice(0, start) + `${character}` + value.slice(end);
			cursorStart = start + character.length - 2;
			cursorEnd = start + character.length - 1;
		} else if (character === 'POW') {
			character = `${character}(x,y)`;
			expression = value.slice(0, start) + `${character}` + value.slice(end);
			cursorStart = start + character.length - 4;
			cursorEnd = start + character.length - 3;
		} else {
			expression = value.slice(0, start) + `${character}` + value.slice(end);
			cursorStart = cursorEnd = start + character.length;
		}

		this.form.patchValue({
			expression: expression,
		});

		this.expressionInput.nativeElement.focus();

		// update cursor to be at the end of insertion
		this.expressionInput.nativeElement.selectionStart = cursorStart;
		this.expressionInput.nativeElement.selectionEnd = cursorEnd;
	}

	submit() {
		let validationObject = new ValidationObject();
		validationObject.expression = this.form.controls.expression?.value;
		validationObject.channelNames = this.insertedChannels;
		this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		this.unitService.validateExpression(validationObject).subscribe({
			next: (response) => {
				if (response.isValid) {
					this.dialogRef.close(this.form.controls.expression?.value);
					this.errorMessage = null;
				} else {
					this.errorMessage = response.errorMessage;
				}
				this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
			},
			error: () => {
				this.errorMessage = 'Expression cannot be saved at this time. Please, try again later.';
				this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
			},
		});
	}
}
